import { useContext } from "react";
import { GetReferralSection } from "./GetReferralSection";
import { FormContextMA } from "../form-context";
import {
  cardiacMedEnum,
  otherPresEnum,
} from "../../patient/clinical-services/service-steps/clinical-assessment/data";
import { H2, Section } from "../components/common-components";
import {
  allergensEnum,
  chronicRhinosinusitisSymptomsEnum,
  infectiveRhinitisSymptomsEnum,
  nasalCongestionEnum,
  nasalSymptomsEnum,
  otherSymptomsEnum,
  redFlagSymptomsEnum,
} from "../../patient/clinical-services/service-steps/signs-and-symptoms/data";
import { getTreatmentDrugs } from "../../patient/clinical-services/service-steps/treatment/data";
import { irritantRhinitisEnum } from "../../patient/clinical-services/service-steps/differential/data";
import { GetMultiDropDownValue } from "../components/data";
import { AlertConfirmationTypes } from "../../../helpers/constant";

export const GetAllReferrals = ({ confirmationType, documentation: propDocumentation }) => {
  const {
    formType,
    treatment,
    preAssessment: preAssessmentFromContext,
    clinicalAssessment,
    signsAndSymptoms,
    differentialAssessment,
  } = useContext(FormContextMA);

  // Prefer preAssessment from passed documentation when rendering referral PDFs
  const preAssessment = (propDocumentation && propDocumentation.preAssessment) || preAssessmentFromContext;
  const allowedReferralTypes = [
    AlertConfirmationTypes.ContinueWithRefer,
    AlertConfirmationTypes.EndService,
  ];

  const allowedConfirmations = confirmationType != null ? [confirmationType] : allowedReferralTypes;

  let drugInducedAssessment = [
    {
      checked:
        !!clinicalAssessment?.cardiacMedications?.length &&
        !clinicalAssessment?.cardiacMedications?.includes("None"),
      label: `Cardiac ${
        !!clinicalAssessment?.cardiacMedications?.length &&
        !clinicalAssessment?.cardiacMedications?.includes("None") &&
        `(${
          clinicalAssessment.cardiacMedications
            .map?.(
              (v) => cardiacMedEnum.options.find((o) => o.value === v)?.header
            )
            .join?.(", ") || ""
        })`
      }`,
    },
    {
      checked:
        !!clinicalAssessment?.otherPrescriptionMedications?.length &&
        !clinicalAssessment?.otherPrescriptionMedications?.includes("None"),
      label: `Other Prescription Medications ${
        !!clinicalAssessment?.otherPrescriptionMedications?.length &&
        !clinicalAssessment?.otherPrescriptionMedications?.includes("None") &&
        `(${
          clinicalAssessment.otherPrescriptionMedications
            .map?.(
              (v) => otherPresEnum.options.find((o) => o.value === v)?.header
            )
            .join?.(", ") || ""
        })`
      }`,
    },
    {
      checked: clinicalAssessment?.oralContraceptives === "Yes",
      label: "Oral Contraceptives",
    },
    {
      checked:
        (!clinicalAssessment?.cardiacMedications?.length ||
          !!clinicalAssessment?.cardiacMedications?.includes("None")) &&
        (!clinicalAssessment?.otherPrescriptionMedications?.length ||
          !!clinicalAssessment?.otherPrescriptionMedications?.includes(
            "None"
          )) &&
        (!clinicalAssessment?.otherPrescriptionMedications?.length ||
          !!clinicalAssessment?.otherPrescriptionMedications?.includes(
            "None"
          )) &&
        clinicalAssessment?.oralContraceptives !== "Yes",
      label: "None",
    },
  ];

  let redFlagAssessment = [
    {
      checked:
        clinicalAssessment?.acuteMedicalConditions?.includes("otitis media"),
      label: `Otitis Media`,
    },
    {
      checked:
        clinicalAssessment?.acuteMedicalConditions?.includes("sinusitis"),
      label: `Chronic Recurrent Sinusitis`,
    },
    {
      checked: clinicalAssessment?.chronicMedicalConditions?.some(
        (v) => v.medicalConditionName === "hypothyroidism"
      ),
      label: `Hypothyroidism${
        clinicalAssessment?.chronicMedicalConditions?.find(
          (v) =>
            v.medicalConditionName === "hypothyroidism" &&
            !v.symptomControlIndicator
        )
          ? " (Symptom not controlled)"
          : ""
      }`,
    },
    {
      checked: clinicalAssessment?.chronicMedicalConditions?.some(
        (v) => v.medicalConditionName === "asthma"
      ),
      label: `Asthma${
        clinicalAssessment?.chronicMedicalConditions?.find(
          (v) =>
            v.medicalConditionName === "asthma" && !v.symptomControlIndicator
        )
          ? " (Symptom not controlled)"
          : ""
      }`,
    },
  ];

  const eyeSymptoms = signsAndSymptoms?.otherSymptoms?.filter(
    (x) => x === "wateryEyes" || x === "itchyEyes"
  );
  const typicalSymptoms = {
    nasal:
      signsAndSymptoms?.nasalSymptoms?.length &&
      !signsAndSymptoms?.nasalSymptoms?.includes("none")
        ? GetMultiDropDownValue(
            nasalSymptomsEnum.options,
            Array.isArray(signsAndSymptoms?.nasalSymptoms)
              ? signsAndSymptoms.nasalSymptoms.join(", ")
              : ""
          )
        : null,
    rashHives:
      !!signsAndSymptoms?.otherSymptoms?.length &&
      !signsAndSymptoms?.otherSymptoms?.includes("none") &&
      !!signsAndSymptoms?.otherSymptoms?.includes?.("skinRash"),
    congestion:
      signsAndSymptoms?.nasalCongestion &&
      signsAndSymptoms?.nasalCongestion !== "noCongestion"
        ? nasalCongestionEnum.options.find(
            (n) => n.value === signsAndSymptoms?.nasalCongestion
          )?.header
        : null,
    eye: GetMultiDropDownValue(
      otherSymptomsEnum.options,
      Array.isArray(eyeSymptoms) ? eyeSymptoms.join(", ") : ""
    ),
    otherAR:
      !!signsAndSymptoms?.otherSymptoms?.length &&
      !signsAndSymptoms?.otherSymptoms?.includes("none")
        ? GetMultiDropDownValue(
            otherSymptomsEnum.options,
            Array.isArray(signsAndSymptoms?.otherSymptoms)
              ? signsAndSymptoms.otherSymptoms.join(", ")
              : ""
          )
        : null,
  };

  // Red-flag symptom selection: exclude "none" and dedupe
  const selectedRedFlagValues = Array.isArray(signsAndSymptoms?.redFlagSymptoms)
    ? signsAndSymptoms.redFlagSymptoms.filter((v) => v !== "none")
    : [];
  const uniqueRedFlagValues = Array.from(new Set(selectedRedFlagValues));
  const redFlagSymptoms =
    uniqueRedFlagValues.length > 0
      ? GetMultiDropDownValue(
          redFlagSymptomsEnum.options,
          uniqueRedFlagValues.join(", ")
        )
      : null;
  let typicalAssessment = [
    {
      checked: !!typicalSymptoms.nasal,
      label: `Nasal Symptoms ${
        typicalSymptoms.nasal && `(${typicalSymptoms.nasal})`
      }`,
    },
    {
      checked: !!typicalSymptoms.congestion,
      label: `Nasal Congestion ${
        typicalSymptoms.congestion && `(${typicalSymptoms.congestion})`
      }`,
    },
    {
      checked: !!typicalSymptoms.eye,
      label: `Eye Symptoms ${
        typicalSymptoms.eye && `(${typicalSymptoms.eye})`
      }`,
    },
    {
      checked: !!typicalSymptoms.otherAR,
      label: `Other Allergic Rhinitis Symptoms ${
        typicalSymptoms.otherAR && `(${typicalSymptoms.otherAR})`
      }`,
    },
    {
      checked: !!typicalSymptoms.rashHives,
      label: `Rash / Hives ${
        typicalSymptoms.rashHives && `(${typicalSymptoms.rashHives})`
      }`,
    },
    {
      checked: Object.values(typicalSymptoms).every((v) => !v),
      label: `No Typical Signs & Symptoms`,
    },
    {
      checked: !typicalSymptoms.nasal && !typicalSymptoms.congestion,
      label: `No Nasal Symptoms`,
    },
  ];

  let nonTypicalAssessment = [
    {
      checked: uniqueRedFlagValues.length > 0,
      label: uniqueRedFlagValues.length > 0
        ? `Yes - Red Flag Symptoms: ${redFlagSymptoms}`
        : "Yes - Red Flag Symptoms",
    },
  ];

  let allergenceAssessment = [
    {
      checked: signsAndSymptoms?.allergens?.length > 0,
      label: `Allergens (
            ${signsAndSymptoms?.allergens?.map(
                (a) => allergensEnum.options.find((o) => o.value === a)?.header
              )
              .join(", ")})`,
    },
  ];

  let infectiveRhinitisAssessment = [
    {
      checked:
        (!signsAndSymptoms?.infectiveRhinosinusitisSymptoms?.length ||
          !!signsAndSymptoms?.infectiveRhinosinusitisSymptoms?.includes?.(
            "none"
          )) &&
        (!signsAndSymptoms?.chronicRhinosinusitisSymptoms?.length ||
          !!signsAndSymptoms?.chronicRhinosinusitisSymptoms?.includes?.(
            "none"
          )),
      label: `Duration of Symptoms: Less than 7 days - No Infective
                    Rhinitis Identified`,
    },
    {
      checked:
        !!signsAndSymptoms?.infectiveRhinosinusitisSymptoms?.length &&
        !signsAndSymptoms?.infectiveRhinosinusitisSymptoms?.includes?.("none"),
      label: `Duration of Symptoms: 7 to 10 days - Infective
                          Rhinosinusitis
                          ${
                            signsAndSymptoms?.infectiveRhinosinusitisSymptoms
                              ?.length &&
                            `(${signsAndSymptoms.infectiveRhinosinusitisSymptoms
                              .map?.(
                                (v) =>
                                  infectiveRhinitisSymptomsEnum.options.find(
                                    (o) => o.value === v && v !== "none"
                                  )?.header
                              )
                              ?.join?.(", ")})`
                          }`,
    },
    {
      checked:
        !!signsAndSymptoms?.chronicRhinosinusitisSymptoms?.length &&
        !signsAndSymptoms?.chronicRhinosinusitisSymptoms?.includes?.("none"),
      label: `Duration of Symptoms: 12 Weeks or longer - Chronic
                    Rhinosinusitis
                          ${
                            signsAndSymptoms?.chronicRhinosinusitisSymptoms
                              ?.length &&
                            `(${signsAndSymptoms.chronicRhinosinusitisSymptoms
                              .map?.(
                                (v) =>
                                  chronicRhinosinusitisSymptomsEnum.options.find(
                                    (o) => o.value === v && v !== "none"
                                  )?.header
                              )
                              ?.join?.(", ")})`
                          }`,
    },
  ];

  let differentialAssessAssessment = [
    {
      checked:
        !!differentialAssessment?.irritantRhinitis?.length &&
        !differentialAssessment?.irritantRhinitis?.includes?.("none"),
      label: `Irritant (Non-Allergic) Rhinitis (${differentialAssessment.irritantRhinitis
        ?.map?.(
          (v) => irritantRhinitisEnum.options.find((o) => o.value === v)?.header
        )
        ?.join(", ")})`,
    },
    {
      checked: differentialAssessment?.occupationalRhinitisIndicator === "Yes",
      label: `Occupational Rhinitis`,
    },
    {
      checked:
        !!differentialAssessment?.structuralOrMechanicalConditions?.length &&
        !differentialAssessment?.structuralOrMechanicalConditions?.includes?.(
          "none"
        ),
      label: `Structural or Mechanical Factors`,
    },
    {
      checked:
        !!differentialAssessment?.systemicConditions?.length &&
        !differentialAssessment?.systemicConditions?.includes?.("none"),
      label: `Systemic Conditions`,
    },
  ];

  let pharmacologicalAssessment = [
    {
      checked:
        treatment?.firstLineTherapyPrescriptions?.length > 0 ||
        treatment?.stepUpTherapyPrescriptions?.length > 0 ||
        treatment?.alternativeFirstLineTherapyPrescriptions?.length > 0 ||
        treatment?.addOnTherapyOralDecongestantsPrescriptions?.length > 0 ||
        treatment?.addOnTherapyIntranasalDecongestantsPrescriptions?.length >
          0 ||
        treatment?.addOnTherapyOralAntihistaminesPrescriptions?.length > 0,
      label: `No Prescription Added`,
    },
  ];

  let nonPharmacologicalAssessment = [];

  const getReferralDetails = (
    confirmationType,
    explaination,
    reason,
    assessment = null
  ) => {
    let details = assessment
      ? assessment
          .filter((x) => x.checked)
          .map((m) => m.label)
          .join(", ")
      : "";

    return {
      reason: reason,
      type: confirmationType ?? 0,
      details: details,
      explaination: explaination,
      decision: `Pharmacist Clinical Decision to ${
        confirmationType === 4 ? "End Service" : "Continue with Service"
      }`,
    };
  };

  return (
    <>
      {allowedConfirmations.includes(
        preAssessment?.ageAlertConfirmationType
      ) && (
        <GetReferralSection
          referralDetails={getReferralDetails(
            preAssessment?.ageAlertConfirmationType,
            preAssessment?.ageAlertExplaination,
            "Age Alert"
          )}
          hideDecision={true}
          formType={formType}
        />
      )}
      {allowedConfirmations.includes(
        preAssessment?.pregnancyAlertConfirmationType
      ) && (
        <GetReferralSection
          referralDetails={getReferralDetails(
            preAssessment?.pregnancyAlertConfirmationType,
            preAssessment?.pregnancyAlertExplaination,
            "Pregnancy Alert"
          )}
          hideDecision={true}
          formType={formType}
        />
      )}
      {allowedConfirmations.includes(
        clinicalAssessment?.redFlagConfirmationType
      ) && (
        <GetReferralSection
          referralDetails={getReferralDetails(
            clinicalAssessment?.redFlagConfirmationType,
            clinicalAssessment?.redFlagExplaination,
            "Red Flag Medical Conditions",
            redFlagAssessment
          )}
          hideDecision={true}
          formType={formType}
        />
      )}
      {allowedConfirmations.includes(
        clinicalAssessment?.drugInducedConfirmationType
      ) && (
        <GetReferralSection
          referralDetails={getReferralDetails(
            clinicalAssessment?.drugInducedConfirmationType,
            clinicalAssessment?.drugInducedExplaination,
            "Drug Induced Allergic Rhinitis",
            drugInducedAssessment
          )}
          hideDecision={true}
          formType={formType}
        />
      )}
      {allowedConfirmations.includes(
        signsAndSymptoms?.nonTypicalConfirmationType
      ) && (
        <GetReferralSection
          referralDetails={getReferralDetails(
            signsAndSymptoms?.nonTypicalConfirmationType,
            signsAndSymptoms?.nonTypicalExplaination,
            "Red Flag Signs & Symptoms (Non-Typical)",
            nonTypicalAssessment
          )}
          hideDecision={true}
          formType={formType}
        />
      )}
      {allowedConfirmations.includes(
        signsAndSymptoms?.typicalConfirmationType
      ) && (
        <GetReferralSection
          referralDetails={getReferralDetails(
            signsAndSymptoms?.typicalConfirmationType,
            signsAndSymptoms?.typicalExplaination,
            "Typical Signs & Symptoms",
            typicalAssessment
          )}
          hideDecision={true}
          formType={formType}
        />
      )}
      {allowedConfirmations.includes(
        signsAndSymptoms?.allergensConfirmationType
      ) && (
        <GetReferralSection
          referralDetails={getReferralDetails(
            signsAndSymptoms?.allergensConfirmationType,
            signsAndSymptoms?.allergensExplaination,
            "Symptoms & Allergens",
            allergenceAssessment
          )}
          hideDecision={true}
          formType={formType}
        />
      )}
      {allowedConfirmations.includes(
        signsAndSymptoms?.infectiveConfirmationType
      ) && (
        <GetReferralSection
          referralDetails={getReferralDetails(
            signsAndSymptoms?.infectiveConfirmationType,
            signsAndSymptoms?.infectiveExplaination,
            "Infective Rhinitis",
            infectiveRhinitisAssessment
          )}
          hideDecision={true}
          formType={formType}
        />
      )}
      {allowedConfirmations.includes(
        differentialAssessment?.differentialConfirmationType
      ) && (
        <GetReferralSection
          referralDetails={getReferralDetails(
            differentialAssessment?.differentialConfirmationType,
            differentialAssessment?.differentialExplaination,
            "Differential Assessment",
            differentialAssessAssessment
          )}
          hideDecision={true}
          formType={formType}
        />
      )}
      {allowedConfirmations.includes(
        treatment?.pharmacologicalHistoryConfirmationType
      ) && (
        <GetReferralSection
          referralDetails={getReferralDetails(
            treatment?.pharmacologicalHistoryConfirmationType,
            treatment?.pharmacologicalExplaination,
            "Treatment History",
            nonPharmacologicalAssessment
          )}
          hideDecision={true}
          formType={formType}
        />
      )}
      {allowedConfirmations.includes(
        treatment?.pharmacologicalConfirmationType
      ) && (
        <GetReferralSection
          referralDetails={getReferralDetails(
            treatment?.pharmacologicalConfirmationType,
            treatment?.pharmacologicalHistoryExplaination,
            "Pharmacological Treatment",
            pharmacologicalAssessment
          )}
          hideDecision={true}
          formType={formType}
        />
      )}
    </>
  );
};
